import type {Model} from '@github-ui/marketplace-common'
import {modelsCatalogPath, type Repository} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {testIdProps} from '@github-ui/test-id-props'
import {InfoIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, IconButton, SelectPanel, useResponsiveValue, type ResponsiveValue} from '@primer/react'
import type {ActionListGroupedListProps, ActionListItemInput as ItemInput} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useMemo, useRef, useState, type HTMLAttributes} from 'react'
import invariant from 'tiny-invariant'
import type {RepoModel} from '@github-ui/github-models-repo'
import {PublisherAvatar} from '../../../components/PublisherAvatar'
import {SidebarSelectionOptions, type GettingStartedPayload} from '../../../types'
import {userHasAccessToModel} from '../../../utils/model-access'

import styles from './ModelSwitcher.module.css'

export const ModelSwitcher = memo(function ModelSwitcherUnmemoized({
  model,
  onSelect,
  handleSetSidebarTab,
  variant,
  repository,
  availableModels,
  isLoadingModels,
}: {
  model?: Model
  onSelect(model: Model | RepoModel): void
  handleSetSidebarTab?: (newTab: SidebarSelectionOptions) => void
  variant?: 'default' | 'compare_button'
  repository?: Repository
  availableModels: Model[] | RepoModel[]
  isLoadingModels: boolean
}) {
  const {restrictedModels} = useRoutePayload<GettingStartedPayload>()

  const [showPanel, setShowPanel] = useState(false)
  const [filter, setFilter] = useState('')

  const selected = useMemo<ItemInput | undefined>(() => {
    if (model) return modelToItem(model)
    return undefined
  }, [model])

  const onSelectCurrentHandler = useCurrentCallback(onSelect)
  const onSelectHandler = useCallback(
    (selectedItemInput: ItemInput | undefined) => {
      if (selectedItemInput) {
        const selectedModel = availableModels.find(m => m.id === selectedItemInput.id)
        if (selectedModel) onSelectCurrentHandler(selectedModel)
      } else if (variant === 'compare_button' && !selectedItemInput && model) {
        onSelectCurrentHandler(model)
      }
    },
    [availableModels, onSelectCurrentHandler, model, variant],
  )

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const filteredModels = useMemo(() => {
    const filterValue = filter.toLowerCase()
    return availableModels.filter(
      m =>
        m.task === 'chat-completion' &&
        (m.friendly_name.toLowerCase().includes(filterValue) || m.publisher.toLowerCase().includes(filterValue)) &&
        userHasAccessToModel(m, restrictedModels),
    )
  }, [availableModels, filter, restrictedModels])

  // Important: avoid changing this const's referential equality, as it triggers a scrollIntoView.
  // For example, avoid defining a `.selected` property on the object.
  // See: https://github.com/primer/react/blob/308fe82909f3d922be0a6582f83e96798678ec78/packages/react/src/FilteredActionList/FilteredActionListWithModernActionList.tsx#L137
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const items = useMemo<ItemInput[]>(() => {
    return filteredModels.map(modelToItem)
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
  }, [filteredModels])

  // If custom models exist we group them based on their key.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const groupMetadata = useMemo<ActionListGroupedListProps['groupMetadata'] | undefined>(() => {
    const customKeys = filteredModels.flatMap(m => (m.isCustom ? [m.publisher] : []))
    if (!customKeys.length) return undefined

    // Include a "Default" group for models that are not BYOK.
    return [...new Set(customKeys), 'Default'].map(name => ({
      header: {title: name, variant: 'filled'},
      groupId: name,
    }))
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
  }, [filteredModels])

  useEffect(() => {
    if (showPanel && selected) {
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-id="${selected.id}"]`)
        if (!node) return
        const container = getNearestScrollContainer(node)

        // If there is no scroll container, say a list with only 2 items in it.
        // fall back to the browser behaviour. Note this may cause the viewport to scroll
        if (!container) {
          node?.scrollIntoView({
            behavior: 'instant',
          })
          return
        }

        container.scrollTop = node.getBoundingClientRect().top - container.getBoundingClientRect().top
      })
    }
  }, [showPanel, selected])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const selectedModel = useMemo(() => {
    return selected?.id ? filteredModels.find(m => m.id === selected.id) : undefined
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
  }, [filteredModels, selected?.id])

  // Using the renderAnchor prop gives us a lot for free, like accessibility props.
  // See: https://github.com/primer/react/blob/308fe82909f3d922be0a6582f83e96798678ec78/packages/react/src/AnchoredOverlay/AnchoredOverlay.tsx#L197-L204
  // See: https://github.com/primer/react/blob/308fe82909f3d922be0a6582f83e96798678ec78/packages/react/src/SelectPanel/SelectPanel.tsx#L379
  const renderAnchor = useCallback(
    (props: HTMLAttributes<HTMLButtonElement>) => (
      <SelectPanelAnchor buttonProps={props} selected={selectedModel} variant={variant} />
    ),
    [selectedModel, variant],
  )

  return (
    <div className="d-flex flex-items-center">
      <SelectPanel
        title={variant === 'compare_button' ? 'Select model' : 'Switch model'}
        placeholderText="Search"
        items={items}
        loading={isLoadingModels}
        open={showPanel}
        onOpenChange={setShowPanel}
        onFilterChange={setFilter}
        selected={selected}
        onSelectedChange={onSelectHandler}
        overlayProps={{width: 'medium', height: 'large'}}
        textInputProps={{
          ['aria-label']: 'Filter models',
        }}
        renderAnchor={renderAnchor}
        secondaryAction={!repository ? <ViewModeModelsAction /> : undefined}
        {...(groupMetadata ? {groupMetadata} : {})}
      />
      {model && variant !== 'compare_button' && handleSetSidebarTab && (
        <div className="hide-lg hide-xl ml-2">
          <IconButton
            icon={InfoIcon}
            aria-label="Show model info"
            onClick={() => handleSetSidebarTab(SidebarSelectionOptions.DETAILS)}
          />
        </div>
      )}
    </div>
  )
})

function SelectPanelAnchor({
  buttonProps,
  selected,
  variant,
}: {
  buttonProps: HTMLAttributes<HTMLButtonElement>
  selected?: Model | RepoModel
  variant?: 'default' | 'compare_button'
}) {
  // Intentionally omitting the `children` prop—we're rendering a custom element below.
  const {children, ...bProps} = buttonProps

  // TODO: buttonProps includes `ref: HTMLButtonElement`, even though Primer's types don't expose it.
  // See: https://github.com/primer/react/blob/308fe82909f3d922be0a6582f83e96798678ec78/packages/react/src/AnchoredOverlay/AnchoredOverlay.tsx#L198
  // The rendered anchor *must* receive this ref—it tells SelectPanel where to anchor.
  // If cherry-picking props later, make sure to forward the ref to the button.
  const ref = (bProps as {ref?: React.Ref<HTMLButtonElement>}).ref
  invariant(ref, 'ref must be passed to SelectPanelAnchor')

  const extraClassNames = 'className' in bProps && (bProps.className as string)

  if (variant === 'compare_button') {
    return (
      <Button
        {...bProps}
        variant="default"
        aria-label="Select model to compare"
        {...testIdProps('compare-model-button')}
      >
        Compare
      </Button>
    )
  }

  return (
    <Button
      {...bProps}
      variant={selected ? 'default' : 'primary'}
      aria-label="Switch model"
      trailingAction={TriangleDownIcon}
      leadingVisual={
        selected ? (
          <PublisherAvatar
            logoUrl={selected.logo_url}
            darkModeIcon={selected.dark_mode_icon}
            publisher={selected.publisher}
            size={18}
          />
        ) : undefined
      }
      alignContent="start"
      className={clsx(
        'd-flex gap-2 flex-items-center',
        styles.selectAnchor,
        {
          ['bgColor-default']: selected,
        },
        extraClassNames,
      )}
      {...testIdProps('model-friendly-name')}
    >
      <div className={styles.modelName}>
        <span className={selected && 'fgColor-muted'}>Model:</span>{' '}
        {selected ? selected.friendly_name : 'Select a Model'}
      </div>
    </Button>
  )
}

function ViewModeModelsAction() {
  const responsiveButtonSizes: ResponsiveValue<'small' | 'medium'> = {narrow: 'medium', regular: 'small'}
  const linkSize = useResponsiveValue(responsiveButtonSizes, 'small')

  return (
    <SelectPanel.SecondaryActionLink href={modelsCatalogPath()} size={linkSize}>
      View all models
    </SelectPanel.SecondaryActionLink>
  )
}

/**
 * A helper hook that keeps a stable reference to a function, that when invoked
 * will always call the latest version of the callback provided.
 * This is useful when you have a callback that is used in a dependency array, or prop
 * to another component that may expect a stable reference, or a callback that is not stable.
 *
 * @example
 *
 * ```tsx
 * function MyComponent(props) {
 *   const callback = useCurrentCallback(props.unstableCallback)
 *   return <Button onClick={callback}>Click me</Button>
 * }
 * ```
 */
// `: any` here gives us the ability to type narrow, where `: unknown` would not
// eg: const cb = useCurrentCallback((foo: string) => boolean)
//          ^? (foo: string) => boolean
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useCurrentCallback<T extends (...args: any[]) => any>(callback: T): T {
  const currentCallback = useRef<T | null>(callback)
  // eslint-disable-next-line react-hooks/refs
  currentCallback.current = callback
  return useCallback((...args: Parameters<T>) => currentCallback.current!(...args), []) as T
}

function getNearestScrollContainer(element: Element, maxDepth = 5) {
  let currentElement: Element | null = element
  let depth = 0
  while (currentElement && depth < maxDepth) {
    if (currentElement.scrollHeight > currentElement.clientHeight) {
      return currentElement
    }
    currentElement = currentElement.parentElement
    depth++
  }
  return null
}

export function modelToItem({
  id,
  logo_url,
  dark_mode_icon,
  publisher,
  friendly_name,
  isCustom,
}: Pick<
  Model | RepoModel,
  'id' | 'logo_url' | 'dark_mode_icon' | 'publisher' | 'friendly_name' | 'isCustom'
>): ItemInput {
  return {
    id,
    leadingVisual() {
      return <PublisherAvatar logoUrl={logo_url} darkModeIcon={dark_mode_icon} publisher={publisher} size={18} />
    },
    text: friendly_name,
    groupId: isCustom ? publisher : 'Default',
  }
}

try{ ModelSwitcher.displayName ||= 'ModelSwitcher' } catch {}
try{ SelectPanelAnchor.displayName ||= 'SelectPanelAnchor' } catch {}
try{ ViewModeModelsAction.displayName ||= 'ViewModeModelsAction' } catch {}
try{ MyComponent.displayName ||= 'MyComponent' } catch {}